import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Percent, Trash2 } from 'lucide-react';

/**
 * SharedCommissionDialog
 * Lets the user pick multiple barbers for a service item and configure:
 *  1. Pool % (% of price that becomes the commission pool)
 *  2. Distribution % per barber (must sum to 100)
 *
 * Props:
 *  - open: bool
 *  - onClose: fn
 *  - item: the cart item being edited
 *  - barbers: list of available barbers (excludes recepcionistas — services only)
 *  - initialDistribution: existing distribution if user is editing
 *  - initialPoolPct: existing pool %
 *  - onSave: (distribution, poolPct) => void
 */
export default function SharedCommissionDialog({
  open, onClose, item, barbers,
  initialDistribution = [], initialPoolPct = 40, onSave,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [shares, setShares] = useState({}); // { barber_id: pct }
  const [poolPct, setPoolPct] = useState(initialPoolPct);
  const [wasOpen, setWasOpen] = useState(false);

  // Only reset state when the dialog transitions from closed to open
  useEffect(() => {
    if (open && !wasOpen) {
      if (initialDistribution.length > 0) {
        setSelectedIds(initialDistribution.map(d => d.barber_id));
        const map = {};
        initialDistribution.forEach(d => { map[d.barber_id] = d.percentage; });
        setShares(map);
        setPoolPct(initialPoolPct);
      } else {
        setSelectedIds([]);
        setShares({});
        setPoolPct(initialPoolPct);
      }
      setWasOpen(true);
    } else if (!open && wasOpen) {
      setWasOpen(false);
    }
  }, [open, wasOpen, initialDistribution, initialPoolPct]);

  const eligibleBarbers = barbers.filter(b => b.role_type !== 'receptionist');

  const toggleBarber = (id) => {
    setSelectedIds(prev => {
      const exists = prev.includes(id);
      const next = exists ? prev.filter(x => x !== id) : [...prev, id];
      // Recalculate equal split by default
      const equal = next.length > 0 ? +(100 / next.length).toFixed(2) : 0;
      const newShares = {};
      next.forEach(bid => {
        newShares[bid] = equal;
      });
      // Ensure total is exactly 100 — adjust last one
      if (next.length > 0) {
        const sumSoFar = next.slice(0, -1).reduce((s, bid) => s + newShares[bid], 0);
        newShares[next[next.length - 1]] = +(100 - sumSoFar).toFixed(2);
      }
      setShares(newShares);
      return next;
    });
  };

  const updateShare = (id, val) => {
    const num = Math.max(0, Math.min(100, parseFloat(val) || 0));
    setShares(prev => ({ ...prev, [id]: num }));
  };

  const splitEqual = () => {
    if (selectedIds.length === 0) return;
    const equal = +(100 / selectedIds.length).toFixed(2);
    const newShares = {};
    selectedIds.forEach(bid => { newShares[bid] = equal; });
    const sumSoFar = selectedIds.slice(0, -1).reduce((s, bid) => s + newShares[bid], 0);
    newShares[selectedIds[selectedIds.length - 1]] = +(100 - sumSoFar).toFixed(2);
    setShares(newShares);
  };

  const totalShare = selectedIds.reduce((s, id) => s + (shares[id] || 0), 0);
  const totalShareRounded = +totalShare.toFixed(2);
  const totalIsValid = Math.abs(totalShareRounded - 100) < 0.01;
  const poolValid = poolPct > 0 && poolPct <= 100;

  const linePrice = (item?.price || 0) * (item?.quantity || 1);
  const poolAmount = linePrice * (poolPct / 100);

  const handleSave = () => {
    if (selectedIds.length < 2) {
      toast.error('Selecciona al menos 2 barberos para compartir comisión');
      return;
    }
    if (!poolValid) {
      toast.error('El % del pool debe estar entre 1 y 100');
      return;
    }
    if (!totalIsValid) {
      toast.error(`La distribución suma ${totalShareRounded}%. Debe sumar 100%.`);
      return;
    }
    const distribution = selectedIds.map(id => ({
      barber_id: id,
      percentage: +shares[id].toFixed(2),
    }));
    onSave(distribution, +poolPct);
  };

  const handleClear = () => {
    onSave(null, null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="shared-commission-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-gold" />
            Compartir Comisión — {item?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Pool % */}
          <div className="bg-zinc-800/40 rounded-sm p-3">
            <Label htmlFor="pool-pct" className="text-zinc-300 text-xs flex items-center gap-1">
              <Percent className="w-3 h-3" />
              % del precio del servicio destinado a comisión total (pool)
            </Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="pool-pct"
                type="number"
                min={1}
                max={100}
                step={1}
                value={poolPct}
                onChange={(e) => setPoolPct(parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700 text-white w-24"
                data-testid="pool-pct-input"
              />
              <span className="text-zinc-400 text-sm">%</span>
              <span className="text-zinc-500 text-xs ml-auto">
                Pool: <span className="text-gold font-semibold">${poolAmount.toFixed(2)}</span> de ${linePrice.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Barber selection */}
          <div>
            <Label className="text-zinc-300 text-xs">Selecciona barberos a compartir</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {eligibleBarbers.map(b => {
                const sel = selectedIds.includes(b.id);
                return (
                  <button
                    type="button"
                    key={b.id}
                    onClick={() => toggleBarber(b.id)}
                    className={`p-2 rounded-sm border text-xs text-left transition-colors ${
                      sel ? 'bg-gold/15 border-gold/50 text-white' : 'bg-zinc-800/40 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                    }`}
                    data-testid={`shared-barber-chip-${b.id}`}
                  >
                    <div className="font-medium">{b.name}</div>
                    <div className="text-[10px] text-zinc-500">{b.role_type === 'stylist' ? 'Estilista' : 'Barbero'}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Distribution editor */}
          {selectedIds.length >= 2 && (
            <div className="bg-zinc-800/40 rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-300 text-xs">Distribución del pool entre barberos</Label>
                <button
                  type="button"
                  onClick={splitEqual}
                  className="text-[10px] text-gold hover:underline"
                  data-testid="split-equal-btn"
                >
                  Repartir equitativo
                </button>
              </div>
              <div className="space-y-2">
                {selectedIds.map(id => {
                  const barber = barbers.find(b => b.id === id);
                  const sharePct = shares[id] || 0;
                  const barberAmount = poolAmount * (sharePct / 100);
                  return (
                    <div key={id} className="flex items-center gap-2" data-testid={`distribution-row-${id}`}>
                      <span className="text-xs text-zinc-300 flex-1 truncate">{barber?.name}</span>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={sharePct}
                        onChange={(e) => updateShare(id, e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white w-20 h-8 text-xs"
                        data-testid={`share-input-${id}`}
                      />
                      <span className="text-zinc-500 text-xs w-4">%</span>
                      <span className="text-gold text-xs w-20 text-right">${barberAmount.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className={`flex items-center justify-between mt-2 pt-2 border-t border-zinc-700 text-xs ${totalIsValid ? 'text-green-400' : 'text-yellow-400'}`}>
                <span>Total distribución</span>
                <span className="font-semibold" data-testid="total-share-display">{totalShareRounded}%</span>
              </div>
            </div>
          )}

          {selectedIds.length === 1 && (
            <p className="text-xs text-yellow-400">Selecciona al menos un barbero más para compartir la comisión.</p>
          )}
        </div>

        <DialogFooter className="flex items-center gap-2">
          {initialDistribution.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClear}
              className="border-red-700 text-red-400 hover:bg-red-900/30 mr-auto"
              data-testid="clear-shared-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Quitar compartido
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} data-testid="shared-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={selectedIds.length < 2 || !totalIsValid || !poolValid}
            className="bg-gold hover:bg-gold/90 text-black"
            data-testid="shared-save-btn"
          >
            Guardar distribución
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
